import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  applyAssignmentToSlot,
  collectAiAssignmentSlots,
  draftResponseToAssignmentRecord,
} from '../utils/courseOutlineAssignments';
import '../components/AssignmentAssistant.css';

const CourseAssignmentDraftModal = ({
  open,
  onClose,
  sections,
  setSections,
  defaultPrimaryConcentration,
  canGenerate = false,
}) => {
  const slots = useMemo(() => collectAiAssignmentSlots(sections), [sections, open]);
  const [selected, setSelected] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [doneCount, setDoneCount] = useState(0);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(slots.map((s) => s.componentId)));
    setError('');
    setProgress('');
    setDoneCount(0);
  }, [open, slots]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleGenerate = useCallback(async () => {
    const toGenerate = slots.filter((s) => selected.has(s.componentId));
    if (!toGenerate.length || !canGenerate) return;

    setGenerating(true);
    setError('');
    setDoneCount(0);

    const token = localStorage.getItem('token');
    let workingSections = sections;

    try {
      for (let i = 0; i < toGenerate.length; i += 1) {
        const slot = toGenerate[i];
        setProgress(`Generating ${i + 1} of ${toGenerate.length}: ${slot.title}`);

        const nested =
          workingSections[slot.sectionIndex]?.subsections?.[slot.subsectionIndex]
            ?.nestedSections?.[slot.nestedIndex];

        const response = await axios.post(
          '/api/ai/assignment-draft',
          {
            messages: [
              {
                role: 'user',
                content: `Create an assignment titled "${slot.title}" for a course lesson "${slot.lessonTitle}" in module "${slot.moduleTitle}". ${slot.summary}`,
              },
            ],
            context: {
              from_course_builder: true,
              pathway_root_id: defaultPrimaryConcentration || null,
              gradingresulttype: 'Points',
              hours: nested?.hourawards?.[0]?.hours || 1,
              existing_title: slot.title,
            },
            options: { max_questions: 8 },
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const record = draftResponseToAssignmentRecord(response.data, {
          title: slot.title,
          pathwayId: defaultPrimaryConcentration,
          componentTaxonomy: nested?.taxonomy,
        });

        if (record) {
          workingSections = applyAssignmentToSlot(workingSections, slot, record);
          setSections(workingSections);
          setDoneCount(i + 1);
        }
      }

      setProgress(`Created ${toGenerate.length} assignment draft(s). Save the course when ready.`);
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      const msg =
        [err.response?.data?.error, err.response?.data?.detail].filter(Boolean).join(': ') ||
        err.message ||
        'Failed to generate assignment draft.';
      setError(msg);
    } finally {
      setGenerating(false);
    }
  }, [
    slots,
    selected,
    canGenerate,
    sections,
    setSections,
    defaultPrimaryConcentration,
    onClose,
  ]);

  if (!open || slots.length === 0) return null;

  return (
    <div
      className="aa-modal-backdrop"
      role="presentation"
      onClick={() => !generating && onClose()}
    >
      <div
        className="aa-modal"
        role="dialog"
        aria-label="Generate assignment drafts"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(560px, 100%)' }}
      >
        <div className="aa-modal__header">
          <h3>Generate assignment drafts?</h3>
          <button
            type="button"
            className="aa-panel__close"
            onClick={() => !generating && onClose()}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p className="aa-modal__intro">
          Course Builder links assignments from your library on save. These AI slots have summaries
          only — generate full question drafts now, or skip and pick/create assignments manually
          later.
        </p>

        {!canGenerate && (
          <p className="aa-modal__intro" style={{ color: '#b45309', background: '#fffbeb' }}>
            Enable <strong>course-builder → Generate assignment draft</strong> (or assignment-builder
            → Generate draft) in Page Access Manager to use batch generation.
          </p>
        )}

        <ul className="aa-modal__list">
          {slots.map((slot) => (
            <li key={slot.componentId} className="aa-modal__item">
              <label>
                <input
                  type="checkbox"
                  checked={selected.has(slot.componentId)}
                  onChange={() => toggle(slot.componentId)}
                  disabled={generating || !canGenerate}
                />
                <span>
                  <strong>{slot.title}</strong>
                  <span className="aa-modal__meta">
                    {slot.moduleTitle} → {slot.lessonTitle}
                  </span>
                  <span className="aa-modal__desc">{slot.summary}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>

        {progress && !error && (
          <p className="aa-modal__intro" style={{ color: '#047857' }}>
            {progress}
            {doneCount > 0 && doneCount < selected.size ? ` (${doneCount} done)` : ''}
          </p>
        )}
        {error && (
          <p className="aa-modal__intro" style={{ color: '#b91c1c' }}>
            {error}
          </p>
        )}

        <div className="aa-modal__actions">
          <button
            type="button"
            className="aa-btn aa-btn--ghost"
            onClick={onClose}
            disabled={generating}
          >
            {canGenerate ? 'Skip for now' : 'Close'}
          </button>
          {canGenerate && (
            <button
              type="button"
              className="aa-btn aa-btn--primary"
              onClick={handleGenerate}
              disabled={generating || selected.size === 0}
            >
              {generating
                ? 'Generating…'
                : `Generate selected (${selected.size})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseAssignmentDraftModal;
